import { useEffect, useRef, useState } from 'react';

// Hooks
import usePrevious from '../../../utils/react/usePrevious';
import useAnimationFrameLoop from '../../../utils/react/useAnimationFrameLoop';

// Utils
import { getInterpolatedPosition } from '../../../utils/animation/springAnimationUtils';

// Types
import { ToolbarList, ToolbarListType, ToolbarState } from '../toolbarTypes';

const INTERPOLATION_STIFFNESS = 300;
const INTERPOLATION_DAMPING = 30;
const INTERPOLATION_PRECISION = 0.1;

type ToolbarTransitionState = {
    renderedToolbars: ToolbarList[];
    focusPosition: number;
    targetPosition: number;
    velocity: number;
    progress: number;
    progressVelocity: number;
    dismissButtonVisible: boolean;
};

enum TransitionDirection {
    FORWARD = 'forward',
    BACKWARD = 'backward',
}

/**
 * returns the position in pixels of the focus index
 * */
const getFocusPosition = (focusIndex: number, toolBarWidth: number): number => focusIndex * toolBarWidth;

/**
 *  determines if the dismiss button should be visible
 */
const getDismissButtonVisible = (
    progress: number,
    activeToolbar?: ToolbarList,
    prevActiveToolbar?: ToolbarList,
): boolean => {
    if (!activeToolbar) return false;

    // if we are transitioning from a non-main toolbar to a main toolbar, hide the dismiss button
    if (activeToolbar.type === ToolbarListType.CREATE_ELEMENT) return false;

    if (prevActiveToolbar?.type !== ToolbarListType.CREATE_ELEMENT) return true;

    // if the previous toolbar was a main toolbar, dismiss button should be visible once we are halfway through the transition
    return progress >= 0.7;
};

/**
 * Hook to manage transitions between toolbars.
 * Animates the adding of a new toolbar, and sliding out the old toolbar, returning to a previous toolbar.
 */
const useToolbarTransitionManager = (
    toolbarState: ToolbarState | null,
    toolBarWidth: number,
    options: {
        disableAnimation?: boolean;
    } = {},
): ToolbarTransitionState => {
    // refs to store the current animation state
    const currToolbarRef = useRef<ToolbarList>();
    const prevToolbarRef = useRef<ToolbarList>();

    const prevToolbarState = usePrevious(toolbarState);

    const [renderedState, setRenderedState] = useState<ToolbarTransitionState>({
        renderedToolbars: [],
        // ideally for performance this would be animated explicity with a ref,
        // but we need to use state so that the toolbar re-renders can be exactly in sync with the animation
        focusPosition: 0,
        targetPosition: 0,
        velocity: 0,
        progress: 0,
        progressVelocity: 0,
        dismissButtonVisible: false,
    });

    const animationController = useAnimationFrameLoop((delta) => {
        setRenderedState((state) => {
            // if we're not animating, just return the current state
            if (state.focusPosition === state.targetPosition) {
                return state;
            }

            // get the next position
            const [nextPosition, nextVelocity] = getInterpolatedPosition(
                delta,
                state.focusPosition,
                state.velocity,
                state.targetPosition,
                INTERPOLATION_STIFFNESS,
                INTERPOLATION_DAMPING,
                INTERPOLATION_PRECISION,
            );

            // get the next progress, interpolating between 0 and 1 for the transition
            const [nextProgress, nextProgressVelocity] = getInterpolatedPosition(
                delta,
                state.progress,
                state.progressVelocity,
                1,
                INTERPOLATION_STIFFNESS,
                INTERPOLATION_DAMPING,
                INTERPOLATION_PRECISION,
            );

            const dismissButtonVisible = getDismissButtonVisible(
                nextProgress,
                currToolbarRef.current,
                prevToolbarRef.current,
            );

            // if we've reached the target position, stop animating + reset state
            if (nextProgress >= 1) {
                const finalToolbars = currToolbarRef.current ? [currToolbarRef.current] : [];

                animationController.stop();

                return {
                    ...state,
                    velocity: 0,
                    focusPosition: 0,
                    targetPosition: 0,
                    progress: 1,
                    progressVelocity: 0,
                    dismissButtonVisible,
                    renderedToolbars: finalToolbars,
                };
            }

            return {
                ...state,
                progress: nextProgress,
                progressVelocity: nextProgressVelocity,
                velocity: nextVelocity,
                focusPosition: nextPosition,
                dismissButtonVisible,
            };
        });
    });

    useEffect(() => {
        if (!toolbarState) return;

        const currIndex = toolbarState.focusIndex;
        const prevIndex = prevToolbarState?.focusIndex ?? 0;

        const currToolbar = toolbarState.toolbars[currIndex];
        const prevToolbar = prevToolbarState?.toolbars[prevIndex];

        // ========================================
        // No transition — toolbar is the same
        // ========================================

        if (!prevToolbar || currToolbar.id === prevToolbar.id || options.disableAnimation) {
            setRenderedState((state) => ({
                ...state,
                renderedToolbars: [currToolbar],
                focusPosition: 0,
                targetPosition: 0,
                velocity: 0,
                progress: 1,
            }));

            return;
        }

        // ========================================
        // Transition
        // ========================================

        // determine the direction of the transition
        const direction = currIndex > prevIndex ? TransitionDirection.FORWARD : TransitionDirection.BACKWARD;

        const transStartIndex = direction === TransitionDirection.FORWARD ? 0 : 1;
        const transEndIndex = direction === TransitionDirection.FORWARD ? 1 : 0;

        const transStartPos = getFocusPosition(transStartIndex, toolBarWidth);
        const transEndPos = getFocusPosition(transEndIndex, toolBarWidth);

        // we only ever render 2 toolbars at a time — the exiting toolbar and the entering toolbar
        const renderedToolbars =
            direction === TransitionDirection.FORWARD ? [prevToolbar, currToolbar] : [currToolbar, prevToolbar];

        currToolbarRef.current = currToolbar;
        prevToolbarRef.current = prevToolbar;

        setRenderedState((state) => ({
            ...state,
            renderedToolbars,
            focusPosition: transStartPos,
            targetPosition: transEndPos,
            progress: 0,
        }));

        animationController.start();
    }, [toolbarState]);

    return renderedState;
};

export default useToolbarTransitionManager;
